import { Collapse, Stack, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { usePostChangeDeviceName } from '@/api/device';
import { useDevice } from '@/api/devices';
import { FormInputText } from '@/components/form/FormInputText';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { ResetUpdateButtonGroup } from '@/components/userAccount/ResetUpdateButtonGroup';
import { useSpotterContext } from '@/context/SpotterContext';
import { useDisclosure } from '@/hooks/useDisclosure';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { borderGrey } from '@/theme';

type DeviceNameFormValues = {
    deviceName: string;
};

const deviceNameDefaultValues = { deviceName: '' };

export const DeviceDisplayNameSection = ({ readOnly }: { readOnly?: boolean }) => {
    const { spotterId } = useSpotterContext();
    const { toggle: toggleEditMode, isOpen: isEditMode, close: disableEditMode } = useDisclosure();
    const { mutateAsync: postChangeDeviceName } = usePostChangeDeviceName();
    const { data: device, isLoading } = useDevice();
    const deviceNameInputRef = useRef<HTMLInputElement>(null);
    const {
        control: deviceNameFormControl,
        reset: deviceNameFormReset,
        handleSubmit: changeDeviceNameHandleSubmit,
        formState: deviceNameFormState,
        getValues: deviceNameGetValues,
    } = useForm<DeviceNameFormValues>({
        defaultValues: deviceNameDefaultValues,
        mode: 'onChange',
    });
    const deviceName = device?.name || '';

    useUnsavedChangesGuard(deviceNameFormState.isDirty, 'device-name');

    const onDeviceNameSubmit = async (formData: DeviceNameFormValues) => {
        if (!spotterId) {
            // eslint-disable-next-line no-console
            console.log('DeviceDisplayNameSection: onDeviceNameSubmit: No Spotter selected');
            return;
        }
        try {
            await postChangeDeviceName({ newName: formData.deviceName, spotterId: spotterId });
            disableEditMode();
        } catch (err) {
            console.error(err);
        }
    };

    const onInputClick = () => {
        if (isEditMode) {
            return;
        }

        toggleEditMode();
        resetDeviceName();
        deviceNameInputRef.current?.select();
    };

    const onInputBlur = () => {
        if (deviceName === deviceNameGetValues().deviceName) {
            onClickCancel();
        }
    };

    const resetDeviceName = () => {
        deviceNameFormReset({ deviceName });
    };

    const onClickCancel = () => {
        resetDeviceName();
        if (isEditMode) {
            toggleEditMode();
        }
    };

    useEffect(() => {
        if (isLoading) {
            return;
        }
        deviceNameFormReset({ deviceName });
    }, [deviceName, isLoading, deviceNameFormReset]);

    if (isLoading) {
        return (
            <Stack gap={2} data-testid="device-details-device-name-section-loading">
                <SectionHeader title="Device display name" />
                <GenericSkeleton lines={2} />
            </Stack>
        );
    }

    return (
        <Stack gap={2} data-testid="device-details-device-name-section">
            <SectionHeader title="Device display name" />
            {readOnly ? (
                <Typography variant="h5" sx={{ ml: 2 }}>
                    {device?.name ?? device?.spotterId}
                </Typography>
            ) : (
                <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                    <FormInputText
                        name="deviceName"
                        control={deviceNameFormControl}
                        label="Device name"
                        inputFieldProps={{
                            slotProps: {
                                htmlInput: {
                                    ref: deviceNameInputRef,
                                },
                            },
                            onClick: onInputClick,
                            onBlur: onInputBlur,
                            id: 'deviceName',
                            type: 'text',
                            size: 'medium',
                            className: isEditMode ? 'edit-mode' : 'view-mode',
                            sx: {
                                '&.view-mode': {
                                    input: { cursor: 'default' },
                                    '.MuiOutlinedInput-input': {
                                        WebkitTextFillColor: 'black',
                                    },
                                    '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: borderGrey,
                                    },
                                },
                                '&.edit-mode': {
                                    '.MuiFormLabel-root': {
                                        color: 'info.main',
                                    },
                                    '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'info.main',
                                    },
                                    '.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'info.main',
                                    },
                                },
                            },
                        }}
                        controllerProps={{
                            rules: {
                                required: 'Field is required',
                            },
                        }}
                    />
                    <Collapse in={isEditMode} orientation="horizontal">
                        <ResetUpdateButtonGroup
                            resetLabel="Cancel"
                            onReset={onClickCancel}
                            onSubmit={changeDeviceNameHandleSubmit(onDeviceNameSubmit)}
                            isResetDisabled={!deviceNameFormState.isDirty || deviceNameFormState.isSubmitting}
                            isUpdateDisabled={!deviceNameFormState.isDirty || deviceNameFormState.isSubmitting}
                            isSubmitting={deviceNameFormState.isSubmitting}
                            containerStyle={{ justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}
                        />
                    </Collapse>
                </Stack>
            )}
        </Stack>
    );
};
